'use client';

import { useState } from 'react';
import SettingsPanel from './SettingsPanel';

type SubjectDocument = {
  filename: string;
  originalName: string;
  uploadedAt: string;
};

type SubjectItem = {
  id: string;
  name: string;
  emoji: string;
  color: string;
};

type CalendarAssignment = {
  id: string;
  title: string;
  dueDate: string;
  subject: string;
  subjectOverride?: string;
};

interface SubjectPickerProps {
  selected: string | null;
  onSelect: (subjectId: string | null) => void;
  onNewChat: () => void;
  onSwitchUser: () => void;
  subjects: SubjectItem[];
  studentName: string;
  documentsBySubject: Record<string, SubjectDocument[]>;
  onDeleteDocument: (subjectId: string, filename: string) => void;
  profileId: string | null;
  calendarConfig: { calendarUrl: string; lastSyncedAt: string | null } | null;
  assignments: CalendarAssignment[];
  onSaveCalendarUrl: () => void;
  onRefreshCalendar: () => void;
  onUpdateAssignmentSubject: (assignmentId: string, subject: string) => void;
  viewMode: 'chat' | 'calendar';
  onViewChange: (mode: 'chat' | 'calendar') => void;
}

const ASSIGNMENTS_UPCOMING_DAYS = 14;

export default function SubjectPicker({
  selected,
  onSelect,
  onNewChat,
  onSwitchUser,
  subjects,
  studentName,
  documentsBySubject,
  onDeleteDocument,
  profileId,
  calendarConfig,
  assignments,
  onSaveCalendarUrl,
  onRefreshCalendar,
  onUpdateAssignmentSubject,
  viewMode,
  onViewChange,
}: SubjectPickerProps) {
  const [openDocsSubjectId, setOpenDocsSubjectId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);

  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() + ASSIGNMENTS_UPCOMING_DAYS);
  const upcomingAssignments = assignments.filter((a) => {
    const d = new Date(a.dueDate);
    return d >= now && d <= cutoff;
  });
  const byDate = new Map<string, CalendarAssignment[]>();
  for (const a of upcomingAssignments) {
    const key = new Date(a.dueDate).toDateString();
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key)!.push(a);
  }
  const sortedDates = Array.from(byDate.keys()).sort(
    (x, y) => new Date(x).getTime() - new Date(y).getTime()
  );

  const getSubjectColor = (subjectId: string) => {
    const s = subjects.find((x) => x.id === subjectId);
    return s?.color ?? '#7B7B8E';
  };

  const effectiveSubject = (a: CalendarAssignment) => a.subjectOverride ?? a.subject;

  return (
    <aside className="sidebar">
      <div className="sidebar__header">
        <div className="sidebar__logo">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <div>
            <h1 className="sidebar__title">Study Buddy</h1>
            <p className="sidebar__subtitle">{studentName}&apos;s Tutor</p>
          </div>
        </div>
      </div>

      <button onClick={onNewChat} className="sidebar__new-chat">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        New chat
      </button>

      <div className="sidebar__view-toggle">
        <button
          type="button"
          className={`sidebar__view-btn ${viewMode === 'chat' ? 'sidebar__view-btn--active' : ''}`}
          onClick={() => onViewChange('chat')}
        >
          Chat
        </button>
        <button
          type="button"
          className={`sidebar__view-btn ${viewMode === 'calendar' ? 'sidebar__view-btn--active' : ''}`}
          onClick={() => onViewChange('calendar')}
        >
          Calendar
        </button>
      </div>

      <div className="sidebar__section">
        <h2 className="sidebar__section-title">Subject</h2>
        <div className="sidebar__subjects">
          <button
            className={`sidebar__subject ${selected === null ? 'sidebar__subject--active' : ''}`}
            onClick={() => onSelect(null)}
          >
            <span className="sidebar__subject-emoji">💡</span>
            <span>General</span>
          </button>
          {subjects.map((subject) => {
            const docCount = documentsBySubject[subject.id]?.length ?? 0;
            const isActive = selected === subject.id;
            const isDocsOpen = openDocsSubjectId === subject.id;

            return (
              <div key={subject.id} className="sidebar__subject-row">
                <button
                  className={`sidebar__subject ${
                    isActive ? 'sidebar__subject--active' : ''
                  }`}
                  onClick={() => onSelect(subject.id)}
                  style={{ '--subject-color': subject.color } as React.CSSProperties}
                >
                  <span className="sidebar__subject-emoji">{subject.emoji}</span>
                  <span>{subject.name}</span>
                </button>
                {docCount > 0 && (
                  <button
                    type="button"
                    className={`sidebar__subject-docs ${
                      isDocsOpen ? 'sidebar__subject-docs--active' : ''
                    }`}
                    title="View uploaded documents for this subject"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDocsSubjectId((prev) =>
                        prev === subject.id ? null : subject.id
                      );
                    }}
                  >
                    <span className="sidebar__subject-docs-icon">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M16.5 6.5L7.5 15.5" />
                        <path d="M14 4l4 4" />
                        <path d="M8 20h8a3 3 0 0 0 3-3v-3" />
                        <path d="M4 7v10a3 3 0 0 0 3 3" />
                      </svg>
                    </span>
                    <span className="sidebar__subject-docs-count">{docCount}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {openDocsSubjectId &&
          documentsBySubject[openDocsSubjectId] &&
          documentsBySubject[openDocsSubjectId].length > 0 && (
            <div className="sidebar__documents">
              <h3 className="sidebar__documents-title">Uploaded documents</h3>
              <ul className="sidebar__documents-list">
                {documentsBySubject[openDocsSubjectId].map((doc) => (
                  <li key={doc.filename} className="sidebar__documents-item">
                    <div className="sidebar__documents-info">
                      <span className="sidebar__documents-name">{doc.originalName}</span>
                      <span className="sidebar__documents-meta">
                        {new Date(doc.uploadedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="sidebar__documents-delete"
                      onClick={() =>
                        onDeleteDocument(openDocsSubjectId, doc.filename)
                      }
                      title="Delete document"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div>

      {profileId && (
        <div className="sidebar__section sidebar__assignments-section">
          <h2 className="sidebar__section-title">Assignments</h2>
          <div className="sidebar__assignments-actions">
            <button
              type="button"
              className="sidebar__assignments-refresh"
              onClick={onRefreshCalendar}
              title="Sync calendar"
            >
              Refresh
            </button>
            {calendarConfig?.lastSyncedAt && (
              <span className="sidebar__assignments-synced">
                Synced {new Date(calendarConfig.lastSyncedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          {sortedDates.length === 0 ? (
            <p className="sidebar__assignments-empty">
              No upcoming assignments. Add a calendar URL in Settings and refresh.
            </p>
          ) : (
            <div className="sidebar__assignments-list">
              {sortedDates.map((dateKey) => (
                <div key={dateKey} className="sidebar__assignments-group">
                  <div className="sidebar__assignments-date">
                    {new Date(dateKey).toLocaleDateString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  {(byDate.get(dateKey) ?? []).map((a) => {
                    const subjId = effectiveSubject(a);
                    const isHighlight = selected === subjId;
                    const isEditing = editingAssignmentId === a.id;
                    return (
                      <div
                        key={a.id}
                        className={`sidebar__assignment-item ${
                          isHighlight ? 'sidebar__assignment-item--highlight' : ''
                        }`}
                      >
                        <span
                          className="sidebar__assignment-dot"
                          style={{ background: getSubjectColor(subjId) }}
                        />
                        <div className="sidebar__assignment-info">
                          <span className="sidebar__assignment-title">{a.title}</span>
                          <span className="sidebar__assignment-due">
                            {new Date(a.dueDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="sidebar__assignment-edit">
                          {isEditing ? (
                            <select
                              value={subjId}
                              onChange={(e) => {
                                onUpdateAssignmentSubject(a.id, e.target.value);
                                setEditingAssignmentId(null);
                              }}
                              onBlur={() => setEditingAssignmentId(null)}
                              className="sidebar__assignment-select"
                              autoFocus
                            >
                              {subjects.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              type="button"
                              className="sidebar__assignment-edit-btn"
                              onClick={() => setEditingAssignmentId(a.id)}
                              title="Change subject"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="sidebar__footer">
        {profileId && (
          <button
            type="button"
            className="sidebar__settings-btn"
            onClick={() => setSettingsOpen(true)}
            title="Settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Settings
          </button>
        )}
        <button type="button" className="sidebar__switch-user" onClick={onSwitchUser}>
          Switch user
        </button>
      </div>

      {profileId && settingsOpen && (
        <div
          className="settings-panel-overlay"
          onClick={() => setSettingsOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setSettingsOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close settings"
        />
      )}
      {profileId && (
        <SettingsPanel
          profileId={profileId}
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          initialCalendarUrl={calendarConfig?.calendarUrl ?? ''}
          onSaved={onSaveCalendarUrl}
        />
      )}
    </aside>
  );
}
